import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { Schema } from "@bw/shared/types/generated";
import { formatDate, getUserDisplayName } from "@bw/shared/utils";
import { useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";

import { PlatformPage } from "@/components/PlatformPage";
import { StatusBadge } from "@/components/StatusBadge";
import { listUsers, type PlatformUser } from "@/shared/api";
import {
  issueBan,
  listAppeals,
  listBans,
  listReports,
  unbanUser,
} from "../api";

const TABS = ["Reports", "Appeals", "Bans"] as const;
const PAGE_SIZE = 50;
type Ban = Schema["BanResponse"];

function LoadingState({ label }: { label: string }) {
  return (
    <Box
      role="status"
      aria-label={label}
      aria-live="polite"
      sx={{ display: "flex", justifyContent: "center", py: 8 }}
    >
      <CircularProgress size={32} />
    </Box>
  );
}

function EmptyQueue({ children }: { children: string }) {
  return (
    <Box role="status" aria-live="polite" sx={{ py: 6, textAlign: "center" }}>
      <Typography color="text.secondary">{children}</Typography>
    </Box>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography color="text.secondary" variant="overline">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
        {value}
      </Typography>
    </Box>
  );
}

function QueueLoadMore({
  label,
  hasNextPage,
  isFetching,
  isError,
  onLoadMore,
}: {
  label: string;
  hasNextPage: boolean;
  isFetching: boolean;
  isError: boolean;
  onLoadMore(): void;
}) {
  if (isError) {
    return (
      <Alert
        severity="error"
        role="alert"
        action={
          <Button
            color="inherit"
            aria-label={`Try again loading more ${label}`}
            onClick={onLoadMore}
          >
            Try again
          </Button>
        }
      >
        We couldn&apos;t load more {label}.
      </Alert>
    );
  }
  if (!hasNextPage) return null;
  return (
    <Button
      variant="outlined"
      disabled={isFetching}
      onClick={onLoadMore}
      sx={{ alignSelf: "center" }}
    >
      {isFetching ? `Loading more ${label}…` : `Load more ${label}`}
    </Button>
  );
}

/** Canonical moderation work queues. Resolution belongs to typed detail pages. */
export function ModerationCenterPage() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(
    location.pathname === "/moderation/bans"
      ? 2
      : location.pathname === "/moderation/appeals"
        ? 1
        : 0,
  );
  const [banTargetInput, setBanTargetInput] = useState("");
  const [banTargetUser, setBanTargetUser] = useState<PlatformUser | null>(null);
  const [banReason, setBanReason] = useState("");
  const [banExpires, setBanExpires] = useState("");
  const [issueBanOpen, setIssueBanOpen] = useState(false);
  const [liftBanTarget, setLiftBanTarget] = useState<Ban | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  const reportsQuery = useInfiniteQuery({
    queryKey: ["platform", "reports", "pending"],
    queryFn: ({ pageParam }) =>
      listReports({ status: "pending", limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce(
        (total, page) => total + page.items.length,
        0,
      );
      return loaded < lastPage.total ? loaded : undefined;
    },
  });
  const appealsQuery = useInfiniteQuery({
    queryKey: ["platform", "appeals", "pending"],
    queryFn: ({ pageParam }) =>
      listAppeals({ status: "pending", limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce(
        (total, page) => total + page.items.length,
        0,
      );
      return loaded < lastPage.total ? loaded : undefined;
    },
  });
  const bansQuery = useInfiniteQuery({
    queryKey: ["platform", "bans"],
    queryFn: ({ pageParam }) =>
      listBans({ active_only: true, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce(
        (total, page) => total + page.items.length,
        0,
      );
      return loaded < lastPage.total ? loaded : undefined;
    },
  });
  const banLookupQuery = useQuery({
    queryKey: ["platform", "ban-user-search", banTargetInput.trim()],
    queryFn: () => listUsers({ search: banTargetInput.trim(), limit: 5 }),
    enabled: activeTab === 2 && banTargetInput.trim().length >= 2,
  });

  const issueBanMutation = useMutation({
    mutationFn: ({
      userId,
      reason,
      expiresAt,
    }: {
      userId: string;
      reason: string;
      expiresAt: string | null;
    }) => issueBan(userId, reason, expiresAt),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["platform", "bans"] });
      setBanTargetInput("");
      setBanTargetUser(null);
      setBanReason("");
      setBanExpires("");
      setIssueBanOpen(false);
      setFeedback("Ban issued.");
    },
  });
  const unbanMutation = useMutation({
    mutationFn: (userId: string) => unbanUser(userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["platform", "bans"] });
      setLiftBanTarget(null);
      setFeedback("Ban lifted.");
    },
  });

  const banTargetId = banTargetUser?.uid ?? banTargetInput.trim();
  const banTargetName = banTargetUser
    ? getUserDisplayName(banTargetUser.display_name, banTargetUser.uid)
    : banTargetId;
  const reports = reportsQuery.data?.pages.flatMap((page) => page.items) ?? [];
  const appeals = appealsQuery.data?.pages.flatMap((page) => page.items) ?? [];
  const bans = bansQuery.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <>
      <PlatformPage title="Moderation Center">
        <Stack spacing={3}>
          {feedback ? (
            <Alert
              severity="success"
              role="status"
              aria-label="Moderation update"
            >
              {feedback}
            </Alert>
          ) : null}
          <Tabs
            value={activeTab}
            onChange={(_, next: number) => {
              setActiveTab(next);
              setFeedback(null);
            }}
            aria-label="Moderation queues"
            variant="scrollable"
            scrollButtons="auto"
          >
            {TABS.map((label) => (
              <Tab key={label} label={label} />
            ))}
          </Tabs>

          {activeTab === 0 ? (
            reportsQuery.isLoading ? (
              <LoadingState label="Loading reports" />
            ) : reportsQuery.isError && !reportsQuery.data ? (
              <Alert
                severity="error"
                role="alert"
                action={
                  <Button
                    color="inherit"
                    onClick={() => void reportsQuery.refetch()}
                  >
                    Try again
                  </Button>
                }
              >
                Failed to load reports.
              </Alert>
            ) : reports.length === 0 ? (
              <EmptyQueue>No pending reports</EmptyQueue>
            ) : (
              <Stack spacing={1.5}>
                {reports.map((report) => (
                  <Paper
                    key={report.report_id}
                    component={RouterLink}
                    to={`/moderation/reports/${encodeURIComponent(report.report_id)}`}
                    variant="outlined"
                    sx={{ p: 2, color: "inherit", textDecoration: "none" }}
                  >
                    <Stack spacing={1}>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ justifyContent: "space-between" }}
                      >
                        <Typography sx={{ fontWeight: 700 }}>
                          {report.reason}
                        </Typography>
                        <StatusBadge status={report.status} />
                      </Stack>
                      <Typography color="text.secondary" variant="body2">
                        {report.target_type} report ·{" "}
                        {formatDate(report.created_at)}
                      </Typography>
                      <Fact
                        label="Reporter"
                        value={getUserDisplayName(
                          report.reporter_display_name,
                          report.reporter_id,
                        )}
                      />
                    </Stack>
                  </Paper>
                ))}
                <QueueLoadMore
                  label="reports"
                  hasNextPage={reportsQuery.hasNextPage}
                  isFetching={reportsQuery.isFetchingNextPage}
                  isError={reportsQuery.isFetchNextPageError}
                  onLoadMore={() => void reportsQuery.fetchNextPage()}
                />
              </Stack>
            )
          ) : activeTab === 1 ? (
            appealsQuery.isLoading ? (
              <LoadingState label="Loading appeals" />
            ) : appealsQuery.isError && !appealsQuery.data ? (
              <Alert
                severity="error"
                role="alert"
                action={
                  <Button
                    color="inherit"
                    onClick={() => void appealsQuery.refetch()}
                  >
                    Try again
                  </Button>
                }
              >
                We couldn&apos;t load appeals.
              </Alert>
            ) : appeals.length === 0 ? (
              <EmptyQueue>No pending appeals</EmptyQueue>
            ) : (
              <Stack spacing={1.5}>
                {appeals.map((appeal) => (
                  <Paper
                    key={appeal.appeal_id}
                    component={RouterLink}
                    to={`/moderation/appeals/${encodeURIComponent(appeal.appeal_id)}`}
                    variant="outlined"
                    sx={{ p: 2, color: "inherit", textDecoration: "none" }}
                  >
                    <Stack spacing={1}>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ justifyContent: "space-between" }}
                      >
                        <Typography sx={{ fontWeight: 700 }}>
                          {appeal.reason}
                        </Typography>
                        <StatusBadge status={appeal.status} />
                      </Stack>
                      <Typography color="text.secondary" variant="body2">
                        Submitted {formatDate(appeal.created_at)}
                      </Typography>
                    </Stack>
                  </Paper>
                ))}
                <QueueLoadMore
                  label="appeals"
                  hasNextPage={appealsQuery.hasNextPage}
                  isFetching={appealsQuery.isFetchingNextPage}
                  isError={appealsQuery.isFetchNextPageError}
                  onLoadMore={() => void appealsQuery.fetchNextPage()}
                />
              </Stack>
            )
          ) : (
            <BansPanel
              bans={bans}
              isLoading={bansQuery.isLoading}
              isError={bansQuery.isError}
              onRetry={() => void bansQuery.refetch()}
              onLift={(ban) => {
                setFeedback(null);
                unbanMutation.reset();
                setLiftBanTarget(ban);
              }}
              unbanPending={unbanMutation.isPending}
              hasNextPage={bansQuery.hasNextPage}
              isFetchingNextPage={bansQuery.isFetchingNextPage}
              isFetchNextPageError={bansQuery.isFetchNextPageError}
              onLoadMore={() => void bansQuery.fetchNextPage()}
              banTargetInput={banTargetInput}
              banTargetUser={banTargetUser}
              banLookupQuery={banLookupQuery}
              setBanTargetInput={setBanTargetInput}
              setBanTargetUser={setBanTargetUser}
              banReason={banReason}
              setBanReason={setBanReason}
              banExpires={banExpires}
              setBanExpires={setBanExpires}
              banTargetId={banTargetId}
              openIssueBan={() => {
                setFeedback(null);
                issueBanMutation.reset();
                setIssueBanOpen(true);
              }}
            />
          )}
        </Stack>
      </PlatformPage>

      <Dialog
        open={issueBanOpen}
        onClose={() => !issueBanMutation.isPending && setIssueBanOpen(false)}
        aria-labelledby="issue-ban-title"
      >
        <DialogTitle id="issue-ban-title">Issue ban</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will restrict the user&apos;s interaction actions until the ban
            is lifted or expires. Public and read-only areas remain available.
          </DialogContentText>
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            <Fact label="User" value={banTargetName} />
            <Fact label="Reason" value={banReason.trim()} />
          </Stack>
          {issueBanMutation.isError ? (
            <Alert severity="error" role="alert" sx={{ mt: 2 }}>
              Failed to issue ban.
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={issueBanMutation.isPending}
            onClick={() => setIssueBanOpen(false)}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={issueBanMutation.isPending}
            onClick={() =>
              issueBanMutation.mutate({
                userId: banTargetId,
                reason: banReason.trim(),
                expiresAt: banExpires
                  ? new Date(banExpires).toISOString()
                  : null,
              })
            }
          >
            Issue ban
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={liftBanTarget !== null}
        onClose={() => !unbanMutation.isPending && setLiftBanTarget(null)}
        aria-labelledby="lift-ban-title"
      >
        <DialogTitle id="lift-ban-title">Lift ban</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove the ban and restore interaction actions for{" "}
            {liftBanTarget
              ? getUserDisplayName(
                  liftBanTarget.user_display_name,
                  liftBanTarget.user_id,
                )
              : "this user"}
            .
          </DialogContentText>
          {unbanMutation.isError ? (
            <Alert severity="error" role="alert" sx={{ mt: 2 }}>
              Failed to lift ban.
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={unbanMutation.isPending}
            onClick={() => setLiftBanTarget(null)}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={unbanMutation.isPending}
            onClick={() =>
              liftBanTarget && unbanMutation.mutate(liftBanTarget.user_id)
            }
          >
            Lift ban
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

interface BansPanelProps {
  bans: Ban[];
  isLoading: boolean;
  isError: boolean;
  onRetry(): void;
  onLift(ban: Ban): void;
  unbanPending: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isFetchNextPageError: boolean;
  onLoadMore(): void;
  banTargetInput: string;
  banTargetUser: PlatformUser | null;
  banLookupQuery: {
    data?: { items: PlatformUser[] };
    isLoading: boolean;
  };
  setBanTargetInput(value: string): void;
  setBanTargetUser(value: PlatformUser | null): void;
  banReason: string;
  setBanReason(value: string): void;
  banExpires: string;
  setBanExpires(value: string): void;
  banTargetId: string;
  openIssueBan(): void;
}

function BansPanel(props: BansPanelProps) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", xl: "1fr 360px" },
        gap: 3,
      }}
    >
      <Box>
        {props.isLoading ? (
          <LoadingState label="Loading bans" />
        ) : props.isError && props.bans.length === 0 ? (
          <Alert
            severity="error"
            role="alert"
            action={
              <Button color="inherit" onClick={props.onRetry}>
                Try again
              </Button>
            }
          >
            Failed to load bans.
          </Alert>
        ) : props.bans.length === 0 ? (
          <EmptyQueue>No active bans</EmptyQueue>
        ) : (
          <Stack spacing={1.5}>
            {props.bans.map((ban) => (
              <Paper key={ban.ban_id} variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Stack
                    direction="row"
                    sx={{
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography sx={{ fontWeight: 700 }}>
                      {getUserDisplayName(ban.user_display_name, ban.user_id)}
                    </Typography>
                    <Button
                      variant="outlined"
                      disabled={props.unbanPending}
                      onClick={() => props.onLift(ban)}
                    >
                      Lift ban
                    </Button>
                  </Stack>
                  <Fact label="Reason" value={ban.reason} />
                  <Fact
                    label="Issued by"
                    value={getUserDisplayName(
                      ban.issued_by_display_name,
                      ban.issued_by,
                    )}
                  />
                  <Fact
                    label="Expires"
                    value={
                      ban.expires_at ? formatDate(ban.expires_at) : "Permanent"
                    }
                  />
                </Stack>
              </Paper>
            ))}
            <QueueLoadMore
              label="bans"
              hasNextPage={props.hasNextPage}
              isFetching={props.isFetchingNextPage}
              isError={props.isFetchNextPageError}
              onLoadMore={props.onLoadMore}
            />
          </Stack>
        )}
      </Box>

      <Paper component="section" variant="outlined" sx={{ p: 2.5 }}>
        <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
          Issue a new ban
        </Typography>
        <Stack spacing={2} sx={{ mt: 2.5 }}>
          <Autocomplete<PlatformUser, false, false, true>
            freeSolo
            options={props.banLookupQuery.data?.items ?? []}
            value={props.banTargetUser}
            inputValue={props.banTargetInput}
            loading={props.banLookupQuery.isLoading}
            filterOptions={(options) => options}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.uid
            }
            isOptionEqualToValue={(option, value) =>
              typeof value !== "string" && option.uid === value.uid
            }
            onInputChange={(_, value, reason) => {
              props.setBanTargetInput(value);
              if (reason === "input" && value !== props.banTargetUser?.uid)
                props.setBanTargetUser(null);
            }}
            onChange={(_, value) => {
              if (typeof value === "string") {
                props.setBanTargetUser(null);
                props.setBanTargetInput(value);
              } else {
                props.setBanTargetUser(value);
                props.setBanTargetInput(value?.uid ?? "");
              }
            }}
            renderOption={(optionProps, option) => (
              <Box component="li" {...optionProps} key={option.uid}>
                <Stack spacing={0.25}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {getUserDisplayName(option.display_name, option.uid)}
                  </Typography>
                  <Typography color="text.secondary" variant="caption">
                    {option.email ?? option.uid}
                  </Typography>
                </Stack>
              </Box>
            )}
            renderInput={(params) => <TextField {...params} label="User" />}
          />
          <TextField
            label="Expires at — local time (optional)"
            type="datetime-local"
            value={props.banExpires}
            onChange={(event) => props.setBanExpires(event.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label="Reason"
            multiline
            minRows={4}
            value={props.banReason}
            onChange={(event) => props.setBanReason(event.target.value)}
          />
          <Button
            color="error"
            variant="contained"
            disabled={!props.banTargetId || !props.banReason.trim()}
            onClick={props.openIssueBan}
          >
            Issue Ban
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
